import { CircleLoader } from '@patrianna/core-components'
import * as React from 'react'
import { useState, useEffect, useCallback } from 'react'

import { findClosestEnabledDate } from '../../_helpers/date-utils'
import { runKeyHandler } from '../../_shared/hooks/useKeyDown'
import { withUtils } from '../../_shared/WithUtils'
import type { WithUtilsProps } from '../../_shared/WithUtils'
import type { MaterialUiPickersDate } from '../../typings/date'

import CalendarHeader from './CalendarHeader'
import Day from './Day'
import DayWrapper from './DayWrapper'
import SlideTransition from './SlideTransition'
import type { SlideDirection } from './SlideTransition'

export interface OutterCalendarProps {
  leftArrowIcon?: React.ReactNode
  rightArrowIcon?: React.ReactNode
  renderDay?: (
    day: MaterialUiPickersDate,
    selectedDate: MaterialUiPickersDate,
    dayInCurrentMonth: boolean,
    dayComponent: React.JSX.Element,
  ) => React.JSX.Element
  allowKeyboardControl?: boolean
  leftArrowButtonProps?: any
  rightArrowButtonProps?: any
  shouldDisableDate?: (day: MaterialUiPickersDate) => boolean
  onMonthChange?: (date: MaterialUiPickersDate) => void | Promise<void>
  loadingIndicator?: React.JSX.Element
}

export interface CalendarProps extends OutterCalendarProps, WithUtilsProps {
  date: MaterialUiPickersDate
  onChange: (date: MaterialUiPickersDate, isFinish?: boolean) => void
  minDate?: MaterialUiPickersDate
  maxDate?: MaterialUiPickersDate
  disablePast?: boolean
  disableFuture?: boolean
}

const Calendar: React.FunctionComponent<CalendarProps> = ({
  date,
  onChange,
  utils,
  minDate,
  maxDate,
  disablePast,
  disableFuture,
  shouldDisableDate,
  allowKeyboardControl = true,
  onMonthChange,
  loadingIndicator,
  leftArrowIcon,
  leftArrowButtonProps,
  rightArrowIcon,
  rightArrowButtonProps,
  renderDay,
}) => {
  const [currentMonth, setCurrentMonth] = useState(() => utils.date(date) || utils.date())
  const [slideDirection, setSlideDirection] = useState<SlideDirection>('left')
  const [loadingQueue, setLoadingQueue] = useState(0)
  const [forceRenderCounter, setForceRenderCounter] = useState(0)

  useEffect(() => {
    setForceRenderCounter((prev) => prev + 1)
  }, [date])

  const validateMinMaxDate = useCallback(
    (day: MaterialUiPickersDate) => {
      const now = utils.date()

      return Boolean(
        (disableFuture && utils.isAfterDay(day, now)) ||
        (disablePast && utils.isBeforeDay(day, now)) ||
        (minDate && utils.isBeforeDay(day, utils.date(minDate))) ||
        (maxDate && utils.isAfterDay(day, utils.date(maxDate))),
      )
    },
    [utils, disableFuture, disablePast, minDate, maxDate],
  )

  const shouldDisableDateInternal = useCallback(
    (day: MaterialUiPickersDate) => {
      return validateMinMaxDate(day) || Boolean(shouldDisableDate && shouldDisableDate(day))
    },
    [validateMinMaxDate, shouldDisableDate],
  )

  const shouldDisablePrevMonth = useCallback(() => {
    const now = utils.date()
    const firstEnabledMonth = utils.startOfMonth(
      disablePast && utils.isAfter(now, utils.date(minDate)) ? now : utils.date(minDate),
    )

    return !utils.isBefore(firstEnabledMonth, currentMonth)
  }, [utils, disablePast, minDate, currentMonth])

  const shouldDisableNextMonth = useCallback(() => {
    const now = utils.date()
    const lastEnabledMonth = utils.startOfMonth(
      disableFuture && utils.isBefore(now, utils.date(maxDate)) ? now : utils.date(maxDate),
    )

    return !utils.isAfter(lastEnabledMonth, currentMonth)
  }, [utils, disableFuture, maxDate, currentMonth])

  const handleDaySelect = useCallback(
    (day: MaterialUiPickersDate, isFinish = true) => {
      onChange(utils.mergeDateAndTime(day, date), isFinish)
    },
    [onChange, utils, date],
  )

  const handleChangeMonth = useCallback(
    (newMonth: MaterialUiPickersDate, newSlideDirection: SlideDirection) => {
      setCurrentMonth(newMonth)
      setSlideDirection(newSlideDirection)

      if (onMonthChange) {
        const returnVal = onMonthChange(newMonth)
        if (returnVal) {
          setLoadingQueue((prev) => prev + 1)
          returnVal.then(() => {
            setLoadingQueue((prev) => Math.max(0, prev - 1))
          })
        }
      }
    },
    [onMonthChange],
  )

  const moveToDay = useCallback(
    (day: MaterialUiPickersDate) => {
      if (day && !shouldDisableDateInternal(day)) {
        if (utils.getMonth(day) !== utils.getMonth(currentMonth)) {
          handleChangeMonth(utils.startOfMonth(day), 'left')
        }
        handleDaySelect(day, false)
      }
    },
    [utils, currentMonth, shouldDisableDateInternal, handleChangeMonth, handleDaySelect],
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      runKeyHandler(event, {
        ArrowUp: () => moveToDay(utils.addDays(date, -7)),
        ArrowDown: () => moveToDay(utils.addDays(date, 7)),
        ArrowLeft: () => moveToDay(utils.addDays(date, 1)),
        ArrowRight: () => moveToDay(utils.addDays(date, -1)),
      })
    },
    [moveToDay, utils, date],
  )

  // Auto-adjust date to closest enabled date
  useEffect(() => {
    if (date && shouldDisableDateInternal(date)) {
      const closestEnabledDate = findClosestEnabledDate({
        date,
        utils,
        minDate: utils.date(minDate),
        maxDate: utils.date(maxDate),
        disablePast: Boolean(disablePast),
        disableFuture: Boolean(disableFuture),
        shouldDisableDate: shouldDisableDateInternal,
      })

      handleDaySelect(closestEnabledDate, false)
    }
  }, [date, shouldDisableDateInternal, utils, minDate, maxDate, disablePast, disableFuture, handleDaySelect])

  const renderDays = useCallback(
    (week: MaterialUiPickersDate[]) => {
      const now = utils.date()
      const selectedDate = date ? utils.startOfDay(date) : null
      const currentMonthNumber = utils.getMonth(currentMonth)

      return week.map((day, index) => {
        const disabled = shouldDisableDateInternal(day)
        const isDayInCurrentMonth = utils.getMonth(day) === currentMonthNumber
        const dayStartOfDay = utils.startOfDay(day)
        const isSelected = selectedDate ? utils.isSameDay(selectedDate, dayStartOfDay) : false
        const isCurrent = utils.isSameDay(dayStartOfDay, now)

        const uniqueKey = `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}-${forceRenderCounter}-${index}`

        let dayComponent = (
          <Day disabled={disabled} current={isCurrent} hidden={!isDayInCurrentMonth} selected={isSelected}>
            {utils.getDayText(day)}
          </Day>
        )

        if (renderDay) {
          dayComponent = renderDay(day, selectedDate, isDayInCurrentMonth, dayComponent)
        }

        return (
          <DayWrapper
            value={day}
            key={uniqueKey}
            disabled={disabled}
            dayInCurrentMonth={isDayInCurrentMonth}
            onSelect={handleDaySelect}
          >
            {dayComponent}
          </DayWrapper>
        )
      })
    },
    [utils, date, currentMonth, shouldDisableDateInternal, handleDaySelect, renderDay, forceRenderCounter],
  )

  const renderWeeks = useCallback(() => {
    const weeks = utils.getWeekArray(currentMonth)

    return weeks.map((week, index) => (
      <div key={`week-${index}-${forceRenderCounter}`} className={'MuiPickersCalendar-week'}>
        {renderDays(week)}
      </div>
    ))
  }, [utils, currentMonth, renderDays, forceRenderCounter])

  const KeyDownListener = ({ onKeyDown }: { onKeyDown: (e: KeyboardEvent) => void }): null => {
    useEffect(() => {
      const handleKeyDownInner = (event: KeyboardEvent) => {
        onKeyDown(event)
      }

      document.addEventListener('keydown', handleKeyDownInner)

      return () => {
        document.removeEventListener('keydown', handleKeyDownInner)
      }
    }, [onKeyDown])

    return null
  }

  const loadingElement = loadingIndicator || <CircleLoader />

  return (
    <React.Fragment>
      {allowKeyboardControl && <KeyDownListener onKeyDown={handleKeyDown} />}

      <CalendarHeader
        currentMonth={currentMonth}
        slideDirection={slideDirection}
        onMonthChange={handleChangeMonth}
        leftArrowIcon={leftArrowIcon}
        leftArrowButtonProps={leftArrowButtonProps}
        rightArrowIcon={rightArrowIcon}
        rightArrowButtonProps={rightArrowButtonProps}
        disablePrevMonth={shouldDisablePrevMonth()}
        disableNextMonth={shouldDisableNextMonth()}
      />

      <SlideTransition
        slideDirection={slideDirection}
        transKey={`${currentMonth.toString()}-${forceRenderCounter}`}
        className={'MuiPickersCalendar-transitionContainer'}
      >
        {loadingQueue > 0 ? (
          <div className={'MuiPickersCalendar-progressContainer'}>{loadingElement}</div>
        ) : (
          <div>{renderWeeks()}</div>
        )}
      </SlideTransition>
    </React.Fragment>
  )
}

export default withUtils()(Calendar)
